import React, { useEffect, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { formatPlaceholder } from '../../../lib/utils';
import { getProjectReleaseVersions } from '../../../modules/jira';
import { useProject } from '../../../context/ProjectContext';
import ExecutionScopeForm from '../../elements/common/execution/ExecutionScopeForm';
import SkeletonLoader from '../../elements/SkeletonLoader';
import TextInput from '../../elements/TextInput';
import { renderUserOptions, showUserDisplayName } from '../../../lib/userUtils';
import { useUser } from '../../../context/UserContext';

const CreateTestCycleForm = ({
  formData,
  setFormData,
  isEditing,
  setResponseMessage = () => {},
  mode,
  jobs,
  statuses,
  purposeOptions,
  typeOptions,
  projectUsers = [],
  isLoading,
  isCycleCompleted = false,
  isScopeDefined,
  setIsFormEdited = () => {},
}) => {
  const [versions, setVersions] = useState([]);
  const { currentUser } = useUser();
  const currentUserId = currentUser?.id;
  const { projectId } = useParams();
  const { currentProject } = useProject();

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: type === 'checkbox' ? checked : value,
    }));
    setIsFormEdited(true);
  };

  const handleInputChange = (id, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    setIsFormEdited(true);

    if (id === 'platforms' && value.length > 0) {
      setResponseMessage(null);
    }
  };

  const handleDropdownChange = (id, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    if (id === 'purpose' || id === 'type') {
      setResponseMessage(null);
    }
    setIsFormEdited(true);
  };

  const setIntialFormData = () => {
    if (statuses) {
      let fetchedStatuses = statuses;
      fetchedStatuses = fetchedStatuses?.filter(
        (status) => status.name !== 'Completed'
      );
      const defaultStatus = fetchedStatuses?.find(
        (status) => status.is_default
      );
      const defaultType = typeOptions?.find((type) => type.name === 'Manual')
      if (defaultStatus && formData.status === '') {
        setFormData((prevData) => ({
          ...prevData,
          status: defaultStatus.id,
          type: defaultType.id
        }));
      }
    }
    if (projectUsers) {
      if (projectUsers.length > 0 && formData.owner === '') {
        setFormData((prevData) => ({
          ...prevData,
          owner: currentUserId ? currentUserId : projectUsers[0].id,
        }));
      }
    }
  };

  useEffect(() => {
    setIntialFormData();
  }, []);

  useEffect(() => {
    const loadVersions = async () => {
      if (!currentProject?.linked_project_id) return;
      try {
        const versionResponse = await getProjectReleaseVersions(
          currentProject.linked_project_id,
          projectId
        );
        if (versionResponse) {
          setVersions(versionResponse.values);
        }
      } catch (error) {
        console.error('Error fetching test case data:', error);
      }
    };
    loadVersions();
  }, [currentProject, projectId]);

  return (
    <div>
      {isLoading ? (
        <SkeletonLoader />
      ) : (
        <Form>
          <Form.Group as={Row} controlId="name">
            <Col>
              <Form.Label className="required" sm={3}>
                Name
              </Form.Label>
              {isEditing ? (
                <TextInput
                  id="name"
                  required
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  placeholder="Enter name"
                  maxLength={150}
                />
              ) : (
                <p className="form-static-text">{formData.name}</p>
              )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="description">
            <Col>
              <Form.Label sm={3}>Description</Form.Label>
              {isEditing && !isCycleCompleted ? (
                <TextInput
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                  maxLength={500}
                  rows={3}
                  type="textarea"
                />
              ) : (
                <p className="form-static-text">
                  {formData.description ||
                    formatPlaceholder('Add a description')}
                </p>
              )}
            </Col>
          </Form.Group>
          <Container fluid className="p-0">
            <Row>
              <Col sm={4}>
                <Form.Group controlId="status">
                  <Form.Label className="required">Status</Form.Label>
                  {isEditing && !isCycleCompleted? (
                    <Form.Select
                      as="select"
                      value={formData.status}
                      onChange={(e) =>
                        handleDropdownChange('status', e.target.value)
                      }
                      className="form-input">
                      {statuses
                        ?.filter((f) => f.name !== 'Completed')
                        .map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.name}
                          </option>
                        ))}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                      {statuses?.find((s) => s.id === formData.status)?.name ||
                        ''}
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group controlId="purpose">
                  <Form.Label className="required">Purpose</Form.Label>
                  {isEditing && mode !== 'edit' && !isCycleCompleted ? (
                    <Form.Select
                      as="select"
                      required
                      value={formData.purpose || ''}
                      onChange={(e) =>
                        handleDropdownChange('purpose', e.target.value)
                      }
                      className="form-input">
                      <option value="" disabled>
                        None
                      </option>
                      {purposeOptions?.map((purpose) => (
                          <option key={purpose.id} value={purpose.id}>
                            {purpose.name}
                          </option>
                        ))}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                     {purposeOptions?.find((s) => s.id === formData.purpose)?.name ||
                        ''}
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group controlId="type">
                  <Form.Label className="required">Test Cycle Type</Form.Label>
                  {isEditing && mode !== 'edit' && !isCycleCompleted ? (
                    <Form.Select
                      as="select"
                      value={formData.type || ''}
                      onChange={(e) =>
                        handleDropdownChange('type', e.target.value)
                      }
                      className="form-input">
                      {typeOptions?.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                      {typeOptions?.find((s) => s.id === formData.type)?.name ||
                        ''}
                    </p>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col sm={4}>
                <Form.Group controlId="version">
                  <Form.Label>Release Version</Form.Label>
                  {isEditing && !isCycleCompleted ? (
                    <Form.Select
                      as="select"
                      value={formData?.releaseVersion}
                      onChange={(e) =>
                        handleDropdownChange('releaseVersion', e.target.value)
                      }
                      className="form-input">
                      <option value="">None</option>
                      {versions?.map((version) => (
                        <option key={version.id} value={version.id}>
                          {version.name}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                      {versions?.find((v) => v.id === formData.releaseVersion)
                        ?.name || 'None'}
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group controlId="owner">
                  <Form.Label className="required">Owner</Form.Label>
                  {isEditing && !isCycleCompleted ? (
                    <Form.Select
                      as="select"
                      value={projectUsers?.some((user) => user.id === formData.owner) ? formData.owner : ""}
                      onChange={handleChange}
                      className="form-input">
                      <option value="" disabled>
                        None
                      </option>
                      {renderUserOptions(projectUsers)}
                    </Form.Select>
                  ) : (
                    <p className="form-static-text">
                     {showUserDisplayName(formData?.owner, projectUsers)}
                    </p>
                  )}
                </Form.Group>
              </Col>
              {typeOptions?.find((t) => t.id === formData.type)?.name === 'Automation'  && (
                <Col sm={4}>
                  <Form.Group controlId="job">
                    <Form.Label>Jenkins Job or Github Workflow</Form.Label>
                    {isEditing && !isCycleCompleted ? (
                      <Form.Select
                        as="select"
                        value={formData.job}
                        onChange={(e) =>
                          handleDropdownChange('job', e.target.value)
                        }
                        className="form-input">
                        <option value="">None</option>
                        {jobs?.map((job, index) => (
                          <option key={index} value={job.id}>
                            {job.name}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <p className="form-static-text">
                        {jobs?.find((s) => s.name === formData.job)?.name ||
                          'None'}
                      </p>
                    )}
                  </Form.Group>
                </Col>
              )}
            </Row>
            {isScopeDefined && (
              <>
                <hr />
                <ExecutionScopeForm
                  values={formData}
                  setFieldValue={handleInputChange}
                  isEditing={isEditing}
                  isCycleCompleted={isCycleCompleted}
                />
              </>
            )}
          </Container>
        </Form>
      )}
    </div>
  );
};

export default CreateTestCycleForm;
